import { AlertTriangle, CheckCircle2, Circle, KeyRound, Loader2, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Section, Segmented, SliderField } from "./Fields";

const STAGES = [
  ["submitting", "Send image to provider"],
  ["queued", "Wait in provider queue"],
  ["generating", "Reconstruct 3D geometry"],
  ["downloading", "Download mesh"],
  ["converting", "Repair & scale mesh"],
  ["completed", "Ready"],
];

export const AiUnavailable = ({ onSwitch }) => (
  <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3" data-testid="ai-unavailable-notice">
    <div className="flex gap-2 items-center text-amber-300 text-sm font-medium"><KeyRound size={15} /> AI provider not configured</div>
    <p className="text-xs text-zinc-400 leading-relaxed">
      Full 3D AI conversion requires an image-to-3D provider API key on the server (see README → AI providers). The free Relief mode works without any key.
    </p>
    <Button size="sm" variant="secondary" data-testid="switch-to-relief-btn" onClick={onSwitch}>Use free Relief mode</Button>
  </div>
);

export const AiJobProgress = ({ job, elapsed, onRetry }) => {
  if (!job) return null;
  const current = job.stage === "fetching_result" ? "downloading" : job.stage;
  const idx = job.status === "completed" ? STAGES.length : STAGES.findIndex(([k]) => k === current);
  return (
    <div className="space-y-3" data-testid="ai-job-progress">
      <ol className="space-y-2">
        {STAGES.map(([key, label], i) => {
          const active = current === key && job.status === "running";
          const done = i < idx || (job.status === "completed" && key === "completed");
          return (
            <li key={key} className={`flex items-center gap-2 text-xs ${active ? "text-white" : done ? "text-zinc-400" : "text-zinc-600"}`} data-testid={`ai-stage-${key}`}>
              {active ? <Loader2 size={13} className="animate-spin text-[#ff5a00]" /> : done ? <CheckCircle2 size={13} className="text-[#ff5a00]" /> : <Circle size={13} />}
              {label}
              {active && key === "queued" && job.queue_position != null && <span className="font-mono text-zinc-500">#{job.queue_position}</span>}
            </li>
          );
        })}
      </ol>
      {job.status === "running" && (
        <p className="font-mono text-[11px] text-zinc-500" data-testid="ai-job-elapsed">{job.stage_label} · {elapsed}s elapsed · typically 30–120s</p>
      )}
      {job.status === "failed" && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 space-y-2" data-testid="ai-job-error">
          <p className="text-xs text-red-300 flex gap-2"><AlertTriangle size={14} className="shrink-0" /> {job.error || "AI conversion failed"}</p>
          {job.error_code === "insufficient_credits" && (
            <p className="text-[11px] text-zinc-400">Add balance via Profile → Universal Key, or use the free Relief mode.</p>
          )}
          <Button size="sm" variant="secondary" onClick={onRetry} data-testid="ai-retry-btn"><RotateCw size={13} className="mr-1" /> Retry</Button>
        </div>
      )}
    </div>
  );
};

export const AiControls = ({ provider, aiParams, setAiParams, limits }) => (
  <div className="space-y-8">
    <Section title="AI reconstruction">
      <p className="text-xs text-zinc-400 leading-relaxed">
        Creates a full volumetric 3D object from one image using <span className="text-white">{provider?.label}</span>. Works best with a single object on a plain background. Uses paid cloud GPU inference — limited to {limits?.ai_per_day} jobs/day ({limits?.ai_left} left).
      </p>
      <SliderField id="ai-size" label="Largest dimension" unit=" mm" value={aiParams.target_size_mm} min={10} max={250} step={1} onChange={(v) => setAiParams({ ...aiParams, target_size_mm: v })} />
    </Section>
    <Section title="Orientation (applies to result)">
      <div className="space-y-2">
        <p className="text-xs text-zinc-300">Rotate X</p>
        <Segmented id="ai-rot-x" value={aiParams.rotation_x} onChange={(v) => setAiParams({ ...aiParams, rotation_x: v })} options={[0, 90, 180, 270].map((d) => ({ value: d, label: `${d}°` }))} />
      </div>
      <div className="space-y-2">
        <p className="text-xs text-zinc-300">Rotate Z</p>
        <Segmented id="ai-rot-z" value={aiParams.rotation_z} onChange={(v) => setAiParams({ ...aiParams, rotation_z: v })} options={[0, 90, 180, 270].map((d) => ({ value: d, label: `${d}°` }))} />
      </div>
    </Section>
  </div>
);
